<template>
  <div ref="noteInputContainerRef" class="note-input-container" :class="{ focused: isInputFocused }">
    <TipTapEditor v-model:content="content" class="tiptap-editor" />
    <div style="display: flex; align-items: center; gap: 8px">
      <div style="flex: 1"></div>
      <div ref="waveformContainerRef" class="waveform-container" :class="{ active: isRecording }"></div>
      <Button v-if="isRecording" size="small" class="stop-button" @click="stopHandler">
        <ButtonIcon icon="fa6-solid:stop" />
        {{ $t("notes.input.stopRecording") }}
      </Button>
      <Button v-else-if="isTranscribing" variant="gray" size="small" class="transcribing-button">
        <LoadingSpinner
          size="small"
          color="gray"
          style="border-color: var(--gray-5); border-top-color: var(--gray-2)"
        />
        {{ $t("notes.input.savingAudio") }}
      </Button>
      <Button v-else variant="outline" size="small" class="record-button" @click="startAudioRecordHandler">
        <ButtonIcon icon="fa6-solid:microphone" style="color: var(--red)" />
        <span>{{ $t("notes.input.startVoiceNote") }}</span>
      </Button>

      <Button
        v-if="!isRecording && !isTranscribing"
        :variant="!content ? 'outline' : 'blue'"
        size="icon-small"
        :disabled="isTranscribing || (!content && !transcription)"
        @click="handleSubmit"
      >
        <ButtonIcon icon="lucide:arrow-up" />
      </Button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useWaveSurferRecorderStandalone } from "@meersagor/wavesurfer-vue";
import { useFocusWithin, useLocalStorage } from "@vueuse/core";
import type { Note } from "@asap/shared/utils/supabase.types";
import { useToast } from "~/composables/useToast";
import { useNotes } from "~/components/note/composables/useNotes";
import { onBeforeRouteLeave } from "vue-router";

export type NoteSubmit = Partial<Pick<Note, "content" | "audio_transcription">>;

type Props = {
  draftStorageKey?: string;
};

type Emits = {
  onSubmit: [input: NoteSubmit];
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const { t } = useI18n();

const content = props.draftStorageKey ? useLocalStorage(props.draftStorageKey, "") : ref("");
const transcription = ref("");
const isTranscribing = ref(false);
const isRecording = ref(false);
const recordingComplete = ref(false);
const audioBlob = ref<Blob>();

const waveformContainerRef = useTemplateRef<HTMLDivElement | null>("waveformContainerRef");
const noteInputContainerRef = useTemplateRef<HTMLDivElement | null>("noteInputContainerRef");

const { focused: isInputFocused } = useFocusWithin(noteInputContainerRef);

const hasUnsavedChanges = computed(() => {
  const isContentEmpty = !content.value || content.value === "<p></p>";
  return !isContentEmpty || !!transcription.value;
});

const setupUnsavedChangesGuard = () => {
  const confirmationMessage = t("notes.input.unsavedChangesConfirmation");

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (hasUnsavedChanges.value) {
      event.preventDefault();
      return confirmationMessage;
    }
  };

  onMounted(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
  });

  onUnmounted(() => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
  });

  onBeforeRouteLeave(() => {
    if (hasUnsavedChanges.value) {
      if (!window.confirm(confirmationMessage)) {
        return false;
      }
    }
  });
};

setupUnsavedChangesGuard();

type WaveSurferRecorderOptions = Parameters<typeof useWaveSurferRecorderStandalone>[0]["options"];

const options: WaveSurferRecorderOptions = {
  height: 48,
  waveColor: "#8f969b",
  progressColor: "#6A24FF",
  barGap: 5,
  barWidth: 5,
  barRadius: 8,
  cursorWidth: 0,
  duration: 80,
};

const { startRecording, stopRecording } = useWaveSurferRecorderStandalone({
  containerRef: waveformContainerRef,
  options,
  recordPluginOptions: {
    continuousWaveform: false,
    renderRecordedAudio: true,
    /**
     * The API limits a payload to 10Mo, which caps how long a conversation can be:
     *   128kbps (default) → ~8 min
     *   48kbps            → ~22 min
     *   32kbps            → ~33 min
     */
    audioBitsPerSecond: 32_000,
  },
});

const startAudioRecordHandler = () => {
  startRecording();
  isRecording.value = true;
  transcription.value = "";
};

const { transcribeAudio: transcribeAudioBlob } = useNotes();

const { addToastError } = useToast();

const transcribeAudio = async (blob: Blob) => {
  try {
    isTranscribing.value = true;
    transcription.value = await transcribeAudioBlob(blob);
  } catch (error) {
    addToastError(
      {
        description: t("notes.input.transcriptionError"),
      },
      error
    );
    throw error;
  } finally {
    isTranscribing.value = false;
  }
};

const stopHandler = async () => {
  const blob = await stopRecording();
  isRecording.value = false;
  audioBlob.value = blob;
  recordingComplete.value = true;
  if (blob) {
    await transcribeAudio(blob);
    handleSubmit();
  }
};

async function handleSubmit() {
  const input: NoteSubmit = { content: content.value, audio_transcription: transcription.value };

  emit("onSubmit", input);

  content.value = "";
  transcription.value = "";
  recordingComplete.value = false;
  isRecording.value = false;
}
</script>

<style scoped lang="scss">
.note-input-container {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 12px;

  &.focused {
    border-color: var(--blue);
    outline: 1px solid var(--blue);
  }
}

.waveform-container {
  width: 150px;
  height: 48px;
  display: none;
  &.active {
    display: block;
  }
}

.tiptap-editor {
  :deep(.tiptap) {
    min-height: 56px;
    transition: font-size 0.3s ease;
  }
}

.stop-button {
  background-color: var(--red-lighter) !important;
  color: var(--red) !important;
  border-color: var(--red-lighter) !important;
  &:hover {
    background-color: var(--red-light) !important;
    border-color: var(--red-light) !important;
  }

  &:active {
    outline-color: var(--red-lighter) !important;
  }
}
</style>
